import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate } from 'react-router-dom'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  LanguageSwitcher,
} from '@/components/ui'
import { Button } from '@/components/ui/button'
import { useAuth } from '@/contexts/auth'
import { useStripeCheckout } from '@/hooks/billing/useSimpleStripeCheckout'
import { useStripePlans } from '@/hooks/usePostHogRemoteConfig'
import { formatCurrency } from '@/lib/utils'

export function LandingPage() {
  const { t, i18n } = useTranslation()
  const { user } = useAuth()
  const navigate = useNavigate()
  const { mutate: createCheckout, isPending } = useStripeCheckout()
  const { data: plans, isLoading: isLoadingPrices, error: priceError } = useStripePlans()

  // Redirect authenticated users to dashboard
  useEffect(() => {
    if (user) {
      navigate('/dashboard')
    }
  }, [user, navigate])

  const handleUpgrade = (plan: 'monthly' | 'annual') => {
    if (!user) {
      // Redirect to login, which will redirect back after login
      navigate('/login', {
        state: {
          returnTo: `/checkout?plan=${plan}`,
        },
      })
      return
    }

    if (!plans) {
      console.error('Stripe plans not loaded')
      return
    }

    const priceId = plan === 'monthly' ? plans.monthly.priceId : plans.annual.priceId

    createCheckout({
      priceId,
      successUrl: `${window.location.origin}/dashboard?upgrade=success`,
      cancelUrl: `${window.location.origin}?upgrade=canceled`,
    })
  }

  const scrollToFeatures = () => {
    document.getElementById('features')?.scrollIntoView({ behavior: 'smooth' })
  }

  // Calculate dynamic pricing values
  let monthlyPrice = '$4.99'
  let annualPrice = '$29.99'
  let annualSavingsAmount = '$29.89'
  let annualSavingsPercent = 50
  let annualPerMonthPrice = '$2.50'

  if (plans) {
    const localeForCurrency = i18n.language === 'pt' ? 'pt-BR' : 'en-US'
    monthlyPrice = formatCurrency(plans.monthly.amount, plans.currency, localeForCurrency)
    annualPrice = formatCurrency(plans.annual.amount, plans.currency, localeForCurrency)
    annualPerMonthPrice = formatCurrency(
      plans.annual.amount / 12,
      plans.currency,
      localeForCurrency
    )

    const totalAnnualCostIfMonthly = plans.monthly.amount * 12
    const savings = totalAnnualCostIfMonthly - plans.annual.amount
    if (savings > 0) {
      annualSavingsAmount = formatCurrency(savings, plans.currency, localeForCurrency)
      annualSavingsPercent = Math.round((savings / totalAnnualCostIfMonthly) * 100)
    }
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 border-b border-border/40 bg-background/80 backdrop-blur-md supports-[backdrop-filter]:bg-background/80">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex h-16 items-center justify-between">
            <div className="flex items-center gap-3">
              <img src="/logo.svg" alt="Longbourn" className="w-8 h-8" />
              <span className="text-xl font-bold bg-gradient-to-r from-primary to-purple bg-clip-text text-transparent">
                Longbourn
              </span>
            </div>
            <div className="flex items-center gap-3">
              <LanguageSwitcher variant="dropdown" />
              <Link to="/login">
                <Button variant="outline" size="sm" className="h-9">
                  {t('auth.signIn')}
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="w-full py-24">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="mx-auto max-w-4xl space-y-8">
            <div className="space-y-6">
              <div className="inline-flex items-center rounded-full border px-3 py-1 text-sm">
                <span className="text-primary font-medium">{t('landing.hero.freeTrial')}</span>
              </div>
              <h1 className="text-4xl font-bold tracking-tight sm:text-6xl lg:text-7xl">
                {t('landing.hero.title')}
                <br />
                <span className="bg-gradient-to-r from-primary via-purple to-primary bg-clip-text text-transparent">
                  {t('landing.hero.subtitle')}
                </span>
              </h1>
              <p className="mx-auto max-w-2xl text-xl text-muted-foreground leading-relaxed">
                {t('landing.hero.description')}
              </p>
            </div>

            <div className="flex flex-col gap-4 sm:flex-row sm:justify-center">
              <Link to="/login">
                <Button size="lg" className="text-lg px-8 py-4 h-12">
                  {t('landing.hero.startFreeTrial')}
                </Button>
              </Link>
              <Button
                variant="outline"
                size="lg"
                className="text-lg px-8 py-4 h-12"
                onClick={scrollToFeatures}
              >
                {t('landing.hero.seeHowItWorks')}
              </Button>
            </div>

            {/* Hero Visual */}
            <div className="mx-auto mt-16 max-w-5xl">
              <Card className="border-0 shadow-2xl bg-gradient-to-br from-card/50 to-muted/30 backdrop-blur overflow-hidden">
                <CardContent className="p-2">
                  <div className="rounded-lg overflow-hidden">
                    <img
                      src="/demo.gif"
                      alt={t('landing.hero.dashboardPreview')}
                      className="w-full h-auto"
                      loading="lazy"
                    />
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="w-full py-24">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-6xl">
            <div className="text-center mb-16 space-y-4">
              <h2 className="text-3xl font-bold">{t('landing.features.title')}</h2>
              <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
                {t('landing.features.subtitle')}
              </p>
            </div>

            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              {/* Book Roulette Feature */}
              <Card className="border-0 shadow-sm hover:shadow-md transition-all duration-200 relative overflow-hidden group">
                <CardHeader className="pb-4">
                  <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center mb-4">
                    <span className="text-white text-2xl">🎲</span>
                  </div>
                  <CardTitle className="flex items-center gap-2">
                    {t('landing.features.bookRoulette.title')}
                    <span className="bg-gradient-to-r from-purple-500 to-pink-500 text-white text-xs px-2 py-1 rounded-full">
                      {t('landing.features.bookRoulette.badge')}
                    </span>
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-0">
                  <CardDescription className="text-base leading-relaxed">
                    {t('landing.features.bookRoulette.description')}
                  </CardDescription>
                </CardContent>
              </Card>

              {/* Smart Analytics */}
              <Card className="border-0 shadow-sm hover:shadow-md transition-all duration-200">
                <CardHeader className="pb-4">
                  <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center mb-4">
                    <span className="text-white text-2xl">📊</span>
                  </div>
                  <CardTitle>{t('landing.features.smartAnalytics.title')}</CardTitle>
                </CardHeader>
                <CardContent className="pt-0">
                  <CardDescription className="text-base leading-relaxed">
                    {t('landing.features.smartAnalytics.description')}
                  </CardDescription>
                </CardContent>
              </Card>

              {/* Barcode Scanner */}
              <Card className="border-0 shadow-sm hover:shadow-md transition-all duration-200">
                <CardHeader className="pb-4">
                  <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-green-500 to-emerald-500 flex items-center justify-center mb-4">
                    <span className="text-white text-2xl">📱</span>
                  </div>
                  <CardTitle>{t('landing.features.barcodeScanner.title')}</CardTitle>
                </CardHeader>
                <CardContent className="pt-0">
                  <CardDescription className="text-base leading-relaxed">
                    {t('landing.features.barcodeScanner.description')}
                  </CardDescription>
                </CardContent>
              </Card>

              {/* Smart Organization */}
              <Card className="border-0 shadow-sm hover:shadow-md transition-all duration-200">
                <CardHeader className="pb-4">
                  <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-orange-500 to-red-500 flex items-center justify-center mb-4">
                    <span className="text-white text-2xl">📚</span>
                  </div>
                  <CardTitle>{t('landing.features.smartOrganization.title')}</CardTitle>
                </CardHeader>
                <CardContent className="pt-0">
                  <CardDescription className="text-base leading-relaxed">
                    {t('landing.features.smartOrganization.description')}
                  </CardDescription>
                </CardContent>
              </Card>

              {/* Reading Tracking */}
              <Card className="border-0 shadow-sm hover:shadow-md transition-all duration-200">
                <CardHeader className="pb-4">
                  <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-indigo-500 to-purple-500 flex items-center justify-center mb-4">
                    <span className="text-white text-2xl">⏱️</span>
                  </div>
                  <CardTitle>{t('landing.features.readingProgress.title')}</CardTitle>
                </CardHeader>
                <CardContent className="pt-0">
                  <CardDescription className="text-base leading-relaxed">
                    {t('landing.features.readingProgress.description')}
                  </CardDescription>
                </CardContent>
              </Card>

              {/* Multi-Language */}
              <Card className="border-0 shadow-sm hover:shadow-md transition-all duration-200">
                <CardHeader className="pb-4">
                  <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-teal-500 to-blue-500 flex items-center justify-center mb-4">
                    <span className="text-white text-2xl">🌐</span>
                  </div>
                  <CardTitle>{t('landing.features.multiLanguage.title')}</CardTitle>
                </CardHeader>
                <CardContent className="pt-0">
                  <CardDescription className="text-base leading-relaxed">
                    {t('landing.features.multiLanguage.description')}
                  </CardDescription>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="w-full bg-muted/30 py-24">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-4xl text-center">
            <div className="space-y-4 mb-16">
              <h2 className="text-3xl font-bold">{t('landing.howItWorks.title')}</h2>
              <p className="text-xl text-muted-foreground">{t('landing.howItWorks.subtitle')}</p>
            </div>

            <div className="grid gap-12 md:grid-cols-3">
              <div className="space-y-6">
                <div className="mx-auto w-16 h-16 rounded-full bg-primary flex items-center justify-center shadow-lg">
                  <span className="text-primary-foreground text-xl font-bold">1</span>
                </div>
                <div className="space-y-3">
                  <h3 className="text-xl font-bold">{t('landing.howItWorks.step1.title')}</h3>
                  <p className="text-muted-foreground leading-relaxed">
                    {t('landing.howItWorks.step1.description')}
                  </p>
                </div>
              </div>

              <div className="space-y-6">
                <div className="mx-auto w-16 h-16 rounded-full bg-primary flex items-center justify-center shadow-lg">
                  <span className="text-primary-foreground text-xl font-bold">2</span>
                </div>
                <div className="space-y-3">
                  <h3 className="text-xl font-bold">{t('landing.howItWorks.step2.title')}</h3>
                  <p className="text-muted-foreground leading-relaxed">
                    {t('landing.howItWorks.step2.description')}
                  </p>
                </div>
              </div>

              <div className="space-y-6">
                <div className="mx-auto w-16 h-16 rounded-full bg-primary flex items-center justify-center shadow-lg">
                  <span className="text-primary-foreground text-xl font-bold">3</span>
                </div>
                <div className="space-y-3">
                  <h3 className="text-xl font-bold">{t('landing.howItWorks.step3.title')}</h3>
                  <p className="text-muted-foreground leading-relaxed">
                    {t('landing.howItWorks.step3.description')}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="w-full py-24">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-6xl text-center">
            {/* Loading state for pricing */}
            {isLoadingPrices && (
              <div className="flex items-center justify-center py-12">
                <div className="text-muted-foreground">Loading pricing...</div>
              </div>
            )}

            {/* Error state for pricing */}
            {priceError && (
              <div className="flex items-center justify-center py-12">
                <div className="text-center">
                  <div className="text-red-600 mb-2">Failed to load pricing</div>
                  <div className="text-sm text-muted-foreground">{priceError}</div>
                </div>
              </div>
            )}

            {/* Show pricing when loaded */}
            {!isLoadingPrices && !priceError && (
              <>
                <div className="space-y-4 mb-16">
                  <div className="inline-flex items-center rounded-full bg-gradient-to-r from-primary/10 to-purple/10 px-4 py-2 text-sm font-medium border">
                    <span className="text-primary">{t('landing.pricing.badge')}</span>
                  </div>
                  <h2 className="text-3xl font-bold">{t('landing.pricing.title')}</h2>
                  <p className="text-xl text-muted-foreground">{t('landing.pricing.subtitle')}</p>
                </div>

                <div className="grid gap-8 md:grid-cols-2 max-w-4xl mx-auto">
                  {/* Plano Mensal */}
                  <Card className="border-0 shadow-lg bg-gradient-to-br from-card to-muted/20 relative">
                    <CardHeader className="text-center space-y-4 pb-6">
                      <div className="space-y-2">
                        <CardTitle className="text-2xl">
                          {t('landing.pricing.monthly.title')}
                        </CardTitle>
                        <div className="flex items-center justify-center gap-2">
                          <span className="text-4xl font-bold">{monthlyPrice}</span>
                          <span className="text-muted-foreground">
                            {t('landing.pricing.monthly.period')}
                          </span>
                        </div>
                      </div>
                      <CardDescription className="text-base">
                        {t('landing.pricing.monthly.description')}
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-6">
                      <ul className="space-y-3 text-left">
                        <li className="flex items-center gap-3">
                          <span className="w-5 h-5 rounded-full bg-primary/10 flex items-center justify-center">
                            <span className="w-2 h-2 rounded-full bg-primary"></span>
                          </span>
                          <span>{t('landing.pricing.features.freeTrial')}</span>
                        </li>
                        <li className="flex items-center gap-3">
                          <span className="w-5 h-5 rounded-full bg-primary/10 flex items-center justify-center">
                            <span className="w-2 h-2 rounded-full bg-primary"></span>
                          </span>
                          <span>{t('landing.pricing.features.unlimitedLibrary')}</span>
                        </li>
                        <li className="flex items-center gap-3">
                          <span className="w-5 h-5 rounded-full bg-primary/10 flex items-center justify-center">
                            <span className="w-2 h-2 rounded-full bg-primary"></span>
                          </span>
                          <span>{t('landing.pricing.features.bookRoulette')}</span>
                        </li>
                        <li className="flex items-center gap-3">
                          <span className="w-5 h-5 rounded-full bg-primary/10 flex items-center justify-center">
                            <span className="w-2 h-2 rounded-full bg-primary"></span>
                          </span>
                          <span>{t('landing.pricing.features.barcodeScanner')}</span>
                        </li>
                        <li className="flex items-center gap-3">
                          <span className="w-5 h-5 rounded-full bg-primary/10 flex items-center justify-center">
                            <span className="w-2 h-2 rounded-full bg-primary"></span>
                          </span>
                          <span>{t('landing.pricing.features.detailedAnalytics')}</span>
                        </li>
                        <li className="flex items-center gap-3">
                          <span className="w-5 h-5 rounded-full bg-primary/10 flex items-center justify-center">
                            <span className="w-2 h-2 rounded-full bg-primary"></span>
                          </span>
                          <span>{t('landing.pricing.features.multiLanguage')}</span>
                        </li>
                      </ul>
                      <Button
                        className="w-full"
                        size="lg"
                        variant="outline"
                        onClick={() => handleUpgrade('monthly')}
                        disabled={isPending || isLoadingPrices}
                      >
                        {isPending ? 'Processando...' : t('landing.pricing.monthly.cta')}
                      </Button>
                    </CardContent>
                  </Card>

                  {/* Plano Anual */}
                  <Card className="border-0 shadow-xl bg-gradient-to-br from-primary/5 to-purple/5 relative overflow-hidden">
                    <div className="absolute top-0 right-0 bg-gradient-to-r from-primary to-purple text-white text-sm font-medium px-4 py-1 rounded-bl-lg">
                      {annualSavingsPercent}% OFF
                    </div>
                    <CardHeader className="text-center space-y-4 pb-6">
                      <div className="space-y-2">
                        <CardTitle className="text-2xl">
                          {t('landing.pricing.annual.title')}
                        </CardTitle>
                        <div className="space-y-1">
                          <div className="flex items-center justify-center gap-2">
                            <span className="text-4xl font-bold">{annualPrice}</span>
                            <span className="text-muted-foreground">
                              {t('landing.pricing.annual.period')}
                            </span>
                          </div>
                          <div className="flex items-center justify-center gap-2 text-sm">
                            <span className="line-through text-muted-foreground">
                              {formatCurrency(
                                (plans?.monthly?.amount || 499) * 12,
                                plans?.currency || 'USD',
                                i18n.language === 'pt' ? 'pt-BR' : 'en-US'
                              )}
                            </span>
                            <span className="text-primary font-medium">
                              {t('common.save')} {annualSavingsAmount}
                            </span>
                          </div>
                        </div>
                      </div>
                      <CardDescription className="text-base">
                        {t('landing.pricing.annual.description', { perMonth: annualPerMonthPrice })}
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-6">
                      <ul className="space-y-3 text-left">
                        <li className="flex items-center gap-3">
                          <span className="w-5 h-5 rounded-full bg-primary/20 flex items-center justify-center">
                            <span className="w-2 h-2 rounded-full bg-primary"></span>
                          </span>
                          <span>{t('landing.pricing.features.freeTrial')}</span>
                        </li>
                        <li className="flex items-center gap-3">
                          <span className="w-5 h-5 rounded-full bg-primary/20 flex items-center justify-center">
                            <span className="w-2 h-2 rounded-full bg-primary"></span>
                          </span>
                          <span>{t('landing.pricing.features.unlimitedLibrary')}</span>
                        </li>
                        <li className="flex items-center gap-3">
                          <span className="w-5 h-5 rounded-full bg-primary/20 flex items-center justify-center">
                            <span className="w-2 h-2 rounded-full bg-primary"></span>
                          </span>
                          <span>{t('landing.pricing.features.bookRoulette')}</span>
                        </li>
                        <li className="flex items-center gap-3">
                          <span className="w-5 h-5 rounded-full bg-primary/20 flex items-center justify-center">
                            <span className="w-2 h-2 rounded-full bg-primary"></span>
                          </span>
                          <span>{t('landing.pricing.features.barcodeScanner')}</span>
                        </li>
                        <li className="flex items-center gap-3">
                          <span className="w-5 h-5 rounded-full bg-primary/20 flex items-center justify-center">
                            <span className="w-2 h-2 rounded-full bg-primary"></span>
                          </span>
                          <span>{t('landing.pricing.features.detailedAnalytics')}</span>
                        </li>
                        <li className="flex items-center gap-3">
                          <span className="w-5 h-5 rounded-full bg-primary/20 flex items-center justify-center">
                            <span className="w-2 h-2 rounded-full bg-primary"></span>
                          </span>
                          <span>{t('landing.pricing.features.multiLanguage')}</span>
                        </li>
                        <li className="flex items-center gap-3">
                          <span className="w-5 h-5 rounded-full bg-primary/20 flex items-center justify-center">
                            <span className="w-2 h-2 rounded-full bg-primary"></span>
                          </span>
                          <span className="font-medium text-primary">
                            {t('landing.pricing.annual.prioritySupport')}
                          </span>
                        </li>
                      </ul>
                      <Button
                        className="w-full"
                        size="lg"
                        onClick={() => handleUpgrade('annual')}
                        disabled={isPending || isLoadingPrices}
                      >
                        {isPending ? 'Processando...' : t('landing.pricing.annual.cta')}
                      </Button>
                    </CardContent>
                  </Card>
                </div>
              </>
            )}
          </div>
        </div>
      </section>
      <section className="w-full bg-gradient-to-br from-purple-500/5 to-pink-500/5 py-24">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-6xl">
            <div className="grid gap-12 lg:grid-cols-2 items-center">
              <div className="space-y-8">
                <div className="space-y-4">
                  <span className="inline-block rounded-full bg-gradient-to-r from-purple-500 to-pink-500 px-4 py-2 text-sm font-medium text-white">
                    {t('landing.spotlight.badge')}
                  </span>
                  <h2 className="text-4xl font-bold leading-tight">
                    {t('landing.spotlight.title')}
                    <span className="bg-gradient-to-r from-purple-500 to-pink-500 bg-clip-text text-transparent">
                      {t('landing.spotlight.titleHighlight')}
                    </span>
                  </h2>
                </div>
                <p className="text-xl text-muted-foreground leading-relaxed">
                  {t('landing.spotlight.description')}
                </p>
                <ul className="space-y-4">
                  <li className="flex items-center gap-4">
                    <div className="w-2 h-2 rounded-full bg-primary"></div>
                    <span className="text-muted-foreground">
                      {t('landing.spotlight.features.customRoulettes')}
                    </span>
                  </li>
                  <li className="flex items-center gap-4">
                    <div className="w-2 h-2 rounded-full bg-primary"></div>
                    <span className="text-muted-foreground">
                      {t('landing.spotlight.features.trackHistory')}
                    </span>
                  </li>
                  <li className="flex items-center gap-4">
                    <div className="w-2 h-2 rounded-full bg-primary"></div>
                    <span className="text-muted-foreground">
                      {t('landing.spotlight.features.neverWorryAgain')}
                    </span>
                  </li>
                </ul>
              </div>
              <Card className="border-0 bg-gradient-to-br from-purple-500/10 to-pink-500/10 shadow-xl">
                <CardContent className="p-12 text-center">
                  <div className="space-y-6">
                    <div className="text-8xl animate-bounce">🎲</div>
                    <div className="space-y-2">
                      <h3 className="text-xl font-bold">
                        {t('landing.spotlight.interactiveRoulette')}
                      </h3>
                      <p className="text-muted-foreground">{t('landing.spotlight.demoText')}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>
      <section className="w-full bg-gradient-to-r from-primary to-purple py-24">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="mx-auto max-w-3xl space-y-8 text-primary-foreground">
            <h2 className="text-4xl font-bold leading-tight">{t('landing.cta.title')}</h2>
            <p className="text-xl opacity-90 leading-relaxed">{t('landing.cta.description')}</p>
            <div className="flex flex-col gap-4 sm:flex-row sm:justify-center">
              <Link to="/login">
                <Button size="lg" variant="secondary" className="text-lg px-8 py-4 h-12">
                  {t('landing.cta.button')}
                </Button>
              </Link>
            </div>
            <p className="text-sm opacity-75">{t('landing.cta.benefits')}</p>
          </div>
        </div>
      </section>
      <footer className="w-full border-t border-border/40 bg-muted/30 py-12">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col items-center justify-between gap-6 sm:flex-row">
            <div className="flex items-center gap-3">
              <img src="/logo.svg" alt="Longbourn" className="w-8 h-8" />
              <span className="text-lg font-bold bg-gradient-to-r from-primary to-purple bg-clip-text text-transparent">
                Longbourn
              </span>
            </div>
            <p className="text-sm text-muted-foreground">{t('landing.footer.madeWith')}</p>
            <div className="flex gap-6">
              <Link
                to="/privacy"
                className="text-sm text-muted-foreground hover:text-foreground transition-colors"
              >
                {t('landing.footer.privacy')}
              </Link>
              <Link
                to="/terms"
                className="text-sm text-muted-foreground hover:text-foreground transition-colors"
              >
                {t('landing.footer.terms')}
              </Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  )
}
